import {
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import React from 'react';
import classNames from 'classnames';

import Badge from 'components/partials/badge/badge';
import IconButton from 'components/partials/button/icon-button';
import Textarea from 'components/partials/textarea/textarea';

import { INPUT_MAX_HEIGHT } from '../../ai-assistant.constants';

import Icons from 'assets/icons';

export interface MessageInputHandle {
  focus: () => void;
}

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  customClass?: string;
  /** Pre-fills the textarea, e.g. with the content of the message being edited. */
  initialValue?: string;
  /** Renders an "Edit" banner above the input with a close action; see `onCancelEditing`. */
  isEditing?: boolean;
  onCancelEditing?: () => void;
}

const MessageInput = forwardRef<MessageInputHandle, MessageInputProps>(
  (
    {
      onSendMessage,
      isLoading = false,
      disabled = false,
      placeholder = 'Ask something',
      rows = 1,
      maxLength = 10000,
      customClass = '',
      initialValue = '',
      isEditing = false,
      onCancelEditing,
    },
    ref
  ): React.JSX.Element => {
    const [message, setMessage] = useState(initialValue);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }));

    const isDisabled = isLoading || disabled;

    const trimmedMessage = message.trim();
    const isSubmitDisabled = !trimmedMessage || isDisabled;

    const resetTextareaHeight = (): void => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    };

    const handleSubmit = (
      e: FormEvent<HTMLFormElement> | KeyboardEvent<HTMLTextAreaElement>
    ): void => {
      e.preventDefault();

      if (isSubmitDisabled) return;

      onSendMessage(trimmedMessage);
      setMessage('');
      resetTextareaHeight();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(e);
      }
    };

    useEffect(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(
          textareaRef.current.scrollHeight,
          INPUT_MAX_HEIGHT
        )}px`;
      }
    }, [message]);

    return (
      <form
        onSubmit={handleSubmit}
        className={classNames('w-full', customClass)}
      >
        <div
          className={classNames(
            'relative',
            isEditing && 'rounded-lg border border-navy-500 overflow-hidden'
          )}
        >
          {isEditing && (
            <div className="flex items-center justify-between bg-gray-100 pl-4 pr-2 py-1">
              <Badge
                text="Edit"
                variant="neutral"
                icon={<Icons.EditPencil customClass="w-3 h-3" />}
              />
              <IconButton
                variant="reserve-text"
                size="sm"
                icon={<Icons.Close />}
                onClick={onCancelEditing}
                aria-label="Cancel editing"
                data-testid="message-input-cancel-edit-button"
              />
            </div>
          )}
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={setMessage}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={isDisabled}
            rows={rows}
            showCharCount={false}
            maxLength={maxLength}
            textareaStyles={classNames(
              'custom-scroll py-3 pl-4 pr-12 text-sm resize-none min-h-[46px] max-h-[200px]',
              isEditing
                ? 'border-0 rounded-none hover:border-0 focus:border-0'
                : 'rounded-lg focus:border-gray-600'
            )}
            textareaWrapperStyles="mb-0"
          />
          <button
            type="submit"
            disabled={isSubmitDisabled}
            aria-label="Send message"
            className="absolute right-3 bottom-2 flex items-center justify-center p-2 rounded-lg disabled:cursor-default enabled:hover:bg-navy-100"
          >
            {isLoading ? (
              <Icons.Spinner customClass="w-4 h-4 animate-spin" />
            ) : (
              <Icons.ArrowUpIndicator
                customClass={classNames('transform rotate-[-45deg]', {
                  'text-navy-500': isSubmitDisabled,
                  'text-navy-800': !isSubmitDisabled,
                })}
              />
            )}
          </button>
        </div>
      </form>
    );
  }
);

MessageInput.displayName = 'MessageInput';

export default MessageInput;
